import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import FilterPanel from "@/components/feature/FilterPanel";
import SortBar from "@/components/feature/SortBar";
import Pagination from "@/components/feature/Pagination";
import { useSeo } from "@/hooks/useSeo";
import { movies } from "@/mocks/movies";
import { emptyFilters, filterMovies, sortMovies, type FilterState, type SortKey } from "@/utils/movieQuery";

const PAGE_SIZE = 12;

function buildInitialFilters(params: URLSearchParams): FilterState {
  const type = params.get("type");
  const genre = params.get("genre");
  return {
    ...emptyFilters,
    types: type ? [type] : [],
    genres: genre ? [genre] : [],
  };
}

export default function Category() {
  const [searchParams] = useSearchParams();
  const paramsKey = searchParams.toString();
  const [filters, setFilters] = useState<FilterState>(() => buildInitialFilters(searchParams));
  const [sort, setSort] = useState<SortKey>("updated");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  // Re-sync when entering the page with a different query (e.g. from nav links)
  useEffect(() => {
    setFilters(buildInitialFilters(new URLSearchParams(paramsKey)));
    setPage(1);
  }, [paramsKey]);

  const categoryName = useMemo(() => {
    if (filters.types.length) return filters.types.join(" / ");
    if (filters.genres.length) return filters.genres.join(" / ");
    return "全部影视";
  }, [filters.types, filters.genres]);

  useSeo(
    `${categoryName} - 看片网站`,
    `看片网站提供丰富的${categoryName}资源，支持多维度筛选和排序，方便您快速找到喜欢的影视作品。`,
    `${categoryName},影视资源,在线电影,电视剧,看片网站`
  );

  const processed = useMemo(() => {
    const filtered = filterMovies(movies, filters);
    return sortMovies(filtered, sort);
  }, [filters, sort]);

  const totalPages = Math.max(1, Math.ceil(processed.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = processed.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const activeFilterCount =
    filters.types.length + filters.genres.length + filters.regions.length + filters.years.length;

  const updateFilters = (next: FilterState) => {
    setFilters(next);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ ...emptyFilters });
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line leading-none"></i>
        <span className="text-foreground-900">影视分类</span>
        {categoryName !== "全部影视" && (
          <>
            <i className="ri-arrow-right-s-line leading-none"></i>
            <span className="text-primary-600">{categoryName}</span>
          </>
        )}
      </nav>

      <div className="mt-4 border-b border-background-200/70 pb-5">
        <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">{categoryName}</h1>
        <p className="mt-2 text-sm text-foreground-600">
          看片网站提供丰富的{categoryName}资源，支持按类型、地区、年份多维度筛选与排序，方便您快速找到喜欢的影视作品。
        </p>
      </div>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row">
        <aside className="hidden w-[260px] shrink-0 lg:block">
          <div className="sticky top-[84px] max-h-[calc(100vh-104px)] overflow-y-auto thin-scroll rounded-lg border border-background-200/70 bg-background-50 p-4">
            <FilterPanel filters={filters} onChange={updateFilters} onReset={resetFilters} />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <SortBar
            sort={sort}
            onChange={(s) => { setSort(s); setPage(1); }}
            total={processed.length}
            onOpenFilter={() => setFilterOpen(true)}
            activeFilters={activeFilterCount}
          />

          {pageItems.length > 0 ? (
            <>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <i className="ri-emotion-unhappy-line text-4xl text-foreground-400"></i>
              <p className="mt-4 font-heading text-lg font-bold text-foreground-900">没有找到符合条件的影视作品</p>
              <p className="mt-1 text-sm text-foreground-500">试试减少筛选条件，或重置后重新浏览</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line leading-none"></i>
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>

      {filterOpen && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setFilterOpen(false)}
            className="absolute inset-0 bg-foreground-950/50"
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading text-base font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setFilterOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-background-200/70"
              >
                <i className="ri-close-line text-lg leading-none"></i>
              </button>
            </div>
            <FilterPanel filters={filters} onChange={updateFilters} onReset={resetFilters} />
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="mt-5 w-full rounded-md bg-primary-500 py-3 text-sm font-bold text-background-50"
            >
              查看 {processed.length} 个结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}